import { Theme } from '@mui/material'

import { getCategoryColor } from '../../core/utils/CategoryUtil'
import { CategoryGuids } from '../../core/constants'
import { Category, DateRangeCategoryTotals, GoalsCopy } from '../../core'
import type { DonutData } from '../../common/components/charts/Donut'

const TOP_CATEGORY_LENGTH = 5

export interface CategoryTotal {
  guid: string
  name: string
  color: string
  categoryTotals?: CategoryTotal[]
  amount: number
}

export interface SpendingData {
  categoryData: CategoryTotal[]
  donutData: DonutData[]
  totalAmount: number
}

/**
 * Rolls all categories up into parent categories and return an array of CategoryTotal items.
 * @param categories - Array of all categories
 * @param theme - The current theme to use for colors
 * @param totals - And array of type DateRangeCategoryTotals
 */
const getTotalsByParentCategory = (
  categories: Category[],
  copy: GoalsCopy,
  theme: Theme,
  totals: DateRangeCategoryTotals[],
): CategoryTotal[] => {
  // Iterate over the category data from BE and roll up all categories into parent categories
  // acc is the accumulated category totals of type CategoryTotal that is initialized as an empty []
  // curr is the current DateRangeCategoryTotal
  return totals.reduce((acc, curr) => {
    // Find category and if it doesn't exist, return acc
    const category = categories.find((c) => c.guid === curr.category_guid)
    if (!category) return acc

    // Find parent category and if it doesn't exist, return acc
    const parentGuid = category.parent_guid ?? category.guid
    const parentCategory = categories.find((c) => c.guid === parentGuid)
    if (!parentCategory) return acc

    // Skip negative totals — they represent credits/income/transfers, not spending
    if (curr.total <= 0) return acc

    const categoryTotal = {
      guid: category.guid,
      name:
        category.guid === parentGuid
          ? `${copy.parent_category_totals_label_general} ${category.name}`
          : category.name,
      color: getCategoryColor(parentCategory.guid, theme),
      amount: curr.total,
    }

    // If parent category has already been added to acc then find it and add the current total,
    // otherwise add the parent category to acc.
    const parentCategoryTotal = acc.find((ct) => ct.guid === parentGuid)
    if (parentCategoryTotal) {
      parentCategoryTotal.amount += curr.total
      parentCategoryTotal.categoryTotals?.push(categoryTotal)
    } else {
      acc.push({
        guid: parentGuid,
        name: parentCategory.name,
        color: getCategoryColor(parentCategory.guid, theme),
        categoryTotals: [categoryTotal],
        amount: curr.total,
      })
    }

    return acc
  }, [] as CategoryTotal[])
}

/**
 * Returns an object of type SpendingData containing a subset of category totals related to spending
 * Excludes Income and Transfers
 *
 * @param categories - Array of all categories
 * @param copy - Localized copy
 * @param theme - The current theme to use for colors
 * @param totals - And array of type DateRangeCategoryTotals
 */
export const getSpendingData = (
  categories: Category[],
  copy: GoalsCopy, // TODO: Replace after `Other` is added to SpendingCopy
  theme: Theme,
  totals: DateRangeCategoryTotals[],
): SpendingData => {
  const parentCategoryTotals = getTotalsByParentCategory(categories, copy, theme, totals)

  // Filter out Income and Transfers from spending totals
  // Sort in decending order by amount
  const spendingTotals = parentCategoryTotals
    .filter(
      (ct) =>
        ct.guid !== CategoryGuids.INCOME &&
        ct.guid !== CategoryGuids.INVESTMENTS &&
        ct.guid !== CategoryGuids.TRANSFER &&
        ct.amount > 0,
    )
    .sort((cat1, cat2) => cat2.amount - cat1.amount)

  // Calculate the total spend
  const totalAmount = spendingTotals.reduce(
    (acc: number, total: CategoryTotal) => acc + total.amount,
    0,
  )

  // Find the number of top categories to include
  const length =
    spendingTotals.length > TOP_CATEGORY_LENGTH ? TOP_CATEGORY_LENGTH : spendingTotals.length

  // Only include the top categories
  const categoryData = spendingTotals.slice(0, length)

  // Map the category data to donut data
  const donutData = categoryData.map((categoryTotal) => ({
    id: categoryTotal.guid,
    color: getCategoryColor(categoryTotal.guid, theme),
    label: categoryTotal.name,
    value: (categoryTotal.amount / totalAmount) * 100,
  }))

  // If there additional categories outside of the top categories, combine them into an Other category
  if (spendingTotals.length > 5) {
    const otherCategories = spendingTotals.slice(TOP_CATEGORY_LENGTH)
    const otherTotal = otherCategories.reduce(
      (acc: number, total: CategoryTotal) => acc + total.amount,
      0,
    )
    categoryData.push({
      guid: 'other',
      color: theme.palette.categories.others || '',
      name: copy.saving_goal_other,
      categoryTotals: otherCategories,
      amount: otherTotal,
    })

    donutData.push({
      id: 'other',
      color: theme.palette.categories.others || '',
      label: copy.saving_goal_other,
      value: (otherTotal / totalAmount) * 100,
    })
  }

  // Return an object of type SpendingData
  return {
    categoryData,
    donutData,
    totalAmount,
  }
}

/**
 * Returns an object of type SpendingData containing a subset of category totals related to income
 * Only includes Income subcategories
 *
 * @param categories - Array of all categories
 * @param monthlyTotals - And array of type DateRangeCategoryTotals
 * @param colors - An array of color strings to use for income colors
 */
export const getIncomeData = (
  categories: Category[],
  monthlyTotals: DateRangeCategoryTotals[],
  colors: string[],
): SpendingData => {
  // Get all income subcategories
  const incomeCategories = categories.filter(
    (category) =>
      category.guid === CategoryGuids.INCOME || category.parent_guid === CategoryGuids.INCOME,
  )

  // Filter, sort & map DateRangeCategoryTotals to CategoryTotal objects
  // The filter return only subcategories with a parent category of Income
  // The sort is in acending order due to amounts being negative
  const incomeTotals: CategoryTotal[] = monthlyTotals
    .filter((total) => incomeCategories.some((category) => category.guid === total.category_guid))
    .sort((total1, total2) => total1.total - total2.total)
    .map((total, index) => {
      return {
        guid: total.category_guid,
        name: categories.find((cat) => cat.guid === total.category_guid)?.name,
        color: colors[index % colors.length],
        categoryTotals: [],
        amount: Math.abs(total.total),
      }
    }) as CategoryTotal[]

  // Calculates the total income amount
  const totalAmount = incomeTotals.reduce(
    (acc: number, total: CategoryTotal) => acc + total.amount,
    0,
  )

  // Maps the income totals to donut data
  const donutData = incomeTotals.map((incomeTotal) => ({
    id: incomeTotal.guid,
    color: incomeTotal.color,
    label: incomeTotal.name,
    value: (incomeTotal.amount / totalAmount) * 100,
  }))

  // Return an object of type SpendingData
  return {
    categoryData: incomeTotals,
    donutData,
    totalAmount,
  }
}
